import React, { useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  Collapse,
  IconButton,
  Autocomplete,
  Chip,
  styled
} from '@mui/material';
import {
  AccountBalance as GrantIcon,
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  Camera as CameraIcon,
  AttachFile as FileIcon
} from '@mui/icons-material';

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    color: '#fff',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.23)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.5)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#00BCD4',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
    '&.Mui-focused': {
      color: '#00BCD4',
    },
  },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  padding: theme.spacing(3),
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(3),
}));

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const CRITERIA_SUGGESTIONS = [
  "Diversity",
  "Impact",
  "Sustainability",
  "Community Need",
  "Innovation",
  "Scalability",
  "Social Justice",
  "Environmental Impact"
];

const GrantCreationForm = ({ onSubmit, isSubmitting }) => {
  const [grantData, setGrantData] = useState({
    grantName: '',
    amountRequested: '',
    fundingSource: '',
    project: '',
    startDate: '',
    endDate: '',
    notes: '',
    selectionCriteria: [],
    documents: [],
    smartContractConditions: '',
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [cameraImage, setCameraImage] = useState(null);
  const [fileNames, setFileNames] = useState([]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setGrantData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => 
      file.type.match('application/pdf|image/*')
    );
    setGrantData(prev => ({
      ...prev,
      documents: validFiles
    }));
    setFileNames(validFiles.map(file => file.name));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await onSubmit(grantData);
      setSuccess('Grant created successfully!');
      resetForm();
    } catch (err) {
      setError(err.message || 'Error creating grant');
    }
  };

  const resetForm = () => {
    setGrantData({
      grantName: '',
      amountRequested: '',
      fundingSource: '',
      project: '',
      startDate: '',
      endDate: '',
      notes: '',
      selectionCriteria: [],
      documents: [],
      smartContractConditions: '',
    });
    setFileNames([]);
    setCameraImage(null);
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Grid container spacing={3}>
        {/* Alerts */}
        <Grid item xs={12}>
          <Collapse in={!!error}>
            <Alert 
              severity="error"
              action={
                <IconButton
                  size="small"
                  onClick={() => setError('')}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
            >
              {error}
            </Alert>
          </Collapse>
          
          <Collapse in={!!success}>
            <Alert 
              severity="success"
              action={
                <IconButton
                  size="small"
                  onClick={() => setSuccess('')}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
            >
              {success}
            </Alert>
          </Collapse>
        </Grid>

        {/* Grant Details */}
        <Grid item xs={12}>
          <StyledPaper>
            <Typography variant="h6" sx={{ mb: 3, color: '#00BCD4', display: 'flex', alignItems: 'center', gap: 1 }}>
              <GrantIcon />
              Grant Details
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  label="Grant Name"
                  name="grantName"
                  value={grantData.grantName}
                  onChange={handleChange}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  label="Amount Requested"
                  name="amountRequested"
                  type="number"
                  value={grantData.amountRequested}
                  onChange={handleChange}
                  required
                  InputProps={{
                    startAdornment: '$'
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  label="Start Date"
                  name="startDate"
                  type="date"
                  value={grantData.startDate}
                  onChange={handleChange}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  label="End Date"
                  name="endDate"
                  type="date"
                  value={grantData.endDate}
                  onChange={handleChange}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={CRITERIA_SUGGESTIONS}
                  value={grantData.selectionCriteria}
                  onChange={(_, newValue) => {
                    setGrantData(prev => ({
                      ...prev,
                      selectionCriteria: newValue
                    }));
                  }}
                  renderInput={(params) => (
                    <StyledTextField
                      {...params}
                      label="Selection Criteria"
                      placeholder="Add criteria"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        key={option}
                        label={option}
                        {...getTagProps({ index })}
                        sx={{
                          backgroundColor: '#00BCD4',
                          color: '#fff'
                        }}
                      />
                    ))
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <StyledTextField
                  fullWidth
                  label="Smart Contract Conditions"
                  name="smartContractConditions"
                  value={grantData.smartContractConditions}
                  onChange={handleChange}
                  multiline
                  rows={4}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  sx={{
                    color: '#00BCD4',
                    borderColor: '#00BCD4',
                    '&:hover': {
                      borderColor: '#00ACC1',
                      backgroundColor: 'rgba(0, 188, 212, 0.1)',
                    }
                  }}
                >
                  Upload Documents
                  <VisuallyHiddenInput 
                    type="file" 
                    multiple 
                    onChange={handleFileUpload}
                    accept="application/pdf,image/*"
                  />
                </Button>
                {fileNames.map((name, index) => (
                  <Typography 
                    key={index}
                    variant="caption"
                    display="block"
                    sx={{ mt: 1, color: 'rgba(255, 255, 255, 0.7)' }}
                  >
                    <FileIcon sx={{ fontSize: 16, mr: 1 }} />
                    {name}
                  </Typography>
                ))}
              </Grid>
            </Grid>
          </StyledPaper>
        </Grid>

        {/* Submit Button */}
        <Grid item xs={12}>
          <Button
            fullWidth
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{
              backgroundColor: '#00BCD4',
              '&:hover': {
                backgroundColor: '#00ACC1',
              },
              '&:disabled': {
                backgroundColor: 'rgba(0, 188, 212, 0.5)',
              }
            }}
          >
            {isSubmitting ? 'Creating Grant...' : 'Create Grant'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default GrantCreationForm; 